import { useState } from "react";
import {
  MoreVertical,
  CalendarClock,
  PlusCircle,
  Trash2,
  Paperclip,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CATEGORIAS } from "@/lib/categorias";
import { setPrazo, deletePrazo, addEventoManual, getPrazo, gerarLinkRastreio } from "@/lib/ocorenApi";

const inputClass =
  "w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]";
const labelClass =
  "block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5";

export const NotaActions = ({ nota, onChanged, onOpenComprovantes, idx = 0 }) => {
  const [prazoOpen, setPrazoOpen] = useState(false);
  const [eventoOpen, setEventoOpen] = useState(false);
  const [rastreioOpen, setRastreioOpen] = useState(false);
  const [rastreioLink, setRastreioLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);

  const obterLinkRastreio = async () => {
    setGeneratingLink(true);
    try {
      const res = await gerarLinkRastreio({
        tipo: "nota",
        numero_nota: nota.numero_nota,
        cnpj_cliente: nota.cnpj_cliente,
      });
      // Cria link absoluto baseado no window.location.origin
      const link = `${window.location.origin}/rastreio/${res.token}`;
      setRastreioLink(link);
      setRastreioOpen(true);
    } catch {
      toast.error("Falha ao gerar link de rastreamento");
    } finally {
      setGeneratingLink(false);
    }
  };

  // ---- Prazo state ----
  const [dataPrevista, setDataPrevista] = useState("");
  const [obs, setObs] = useState("");
  const [savingPrazo, setSavingPrazo] = useState(false);

  // ---- Evento manual state ----
  const [categoria, setCategoria] = useState("entregue");
  const [dataEvento, setDataEvento] = useState("");
  const [descricao, setDescricao] = useState("");
  const [codigo, setCodigo] = useState("");
  const [savingEvento, setSavingEvento] = useState(false);

  const openPrazo = async () => {
    setPrazoOpen(true);
    try {
      const p = await getPrazo(nota.numero_nota, nota.cnpj_cliente);
      setDataPrevista(p?.data_entrega_prevista || "");
      setObs(p?.observacao || "");
    } catch {
      /* ignore */
    }
  };

  const salvarPrazo = async () => {
    if (!dataPrevista) {
      toast.error("Informe a data de entrega prevista");
      return;
    }
    setSavingPrazo(true);
    try {
      await setPrazo({
        numero_nota: nota.numero_nota,
        cnpj_cliente: nota.cnpj_cliente,
        data_entrega_prevista: dataPrevista,
        observacao: obs,
      });
      toast.success("Data de entrega prevista salva");
      setPrazoOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar prazo");
    } finally {
      setSavingPrazo(false);
    }
  };

  const removerPrazo = async () => {
    setSavingPrazo(true);
    try {
      await deletePrazo(nota.numero_nota, nota.cnpj_cliente);
      toast.success("Data prevista removida");
      setDataPrevista("");
      setObs("");
      setPrazoOpen(false);
      onChanged?.();
    } catch {
      toast.error("Falha ao remover");
    } finally {
      setSavingPrazo(false);
    }
  };

  const salvarEvento = async () => {
    if (!descricao.trim()) {
      toast.error("Informe a descrição da etapa");
      return;
    }
    if (!dataEvento) {
      toast.error("Informe a data e hora do evento");
      return;
    }
    setSavingEvento(true);
    try {
      await addEventoManual({
        numero_nota: nota.numero_nota,
        cnpj_cliente: nota.cnpj_cliente,
        serie: nota.serie,
        categoria,
        data_ocorrencia: dataEvento,
        descricao_ocorrencia: descricao.trim(),
        codigo_ocorrencia: codigo.trim() || undefined,
      });
      toast.success("Etapa manual adicionada à timeline");
      setEventoOpen(false);
      setDescricao("");
      setCodigo("");
      setDataEvento("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao adicionar etapa");
    } finally {
      setSavingEvento(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`nota-actions-trigger-${idx}`}
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 rounded-sm text-[#52525B] hover:text-[#09090B] hover:bg-[#FAFAFA] transition-colors"
            aria-label="Ações da nota"
          >
            <MoreVertical size={15} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-[#52525B]">
            Ações — NF {nota.numero_nota}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={`nota-action-prazo-${idx}`}
            onClick={openPrazo}
            className="cursor-pointer gap-2"
          >
            <CalendarClock size={14} />
            Definir data de entrega prevista
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`nota-action-evento-${idx}`}
            onClick={() => setEventoOpen(true)}
            className="cursor-pointer gap-2"
          >
            <PlusCircle size={14} />
            Adicionar etapa manual
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`nota-action-comprovante-${idx}`}
            onClick={() => onOpenComprovantes?.(nota)}
            className="cursor-pointer gap-2"
          >
            <Paperclip size={14} />
            Anexar comprovante
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={`nota-action-rastreio-${idx}`}
            onClick={obterLinkRastreio}
            disabled={generatingLink}
            className="cursor-pointer gap-2"
          >
            <Share2 size={14} />
            {generatingLink ? "Gerando link..." : "Gerar link de rastreio"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog: Link de Rastreio Público */}
      <Dialog open={rastreioOpen} onOpenChange={setRastreioOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Link de Rastreamento Público</DialogTitle>
            <DialogDescription>
              Envie este link permanente para seu cliente acompanhar o status de entrega da NF {nota.numero_nota} de forma 100% segura e em tempo real.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={rastreioLink}
                className="flex-1 px-3 py-2 text-xs border border-[#E4E4E7] rounded-md bg-[#FAFAFA] text-[#09090B] font-mono"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(rastreioLink);
                  toast.success("Link de rastreamento copiado para a área de transferência!");
                }}
                className="px-3 py-2 text-xs font-semibold bg-[#09090B] text-white hover:bg-zinc-800 rounded-md transition-colors whitespace-nowrap"
              >
                Copiar
              </button>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setRastreioOpen(false)}
              className="px-3 py-2 text-xs border border-[#E4E4E7] text-[#09090B] hover:bg-[#FAFAFA] rounded-md transition-colors"
            >
              Fechar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Definir prazo */}
      <Dialog open={prazoOpen} onOpenChange={setPrazoOpen}>
        <DialogContent data-testid="prazo-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Data de entrega prevista</DialogTitle>
            <DialogDescription>
              NF {nota.numero_nota}. Define o prazo de entrega; após essa data,
              sem confirmação de entrega, a nota entra em atraso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className={labelClass}>Data prevista</label>
              <input
                data-testid="prazo-input-data"
                type="date"
                value={dataPrevista}
                onChange={(e) => setDataPrevista(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Observação (opcional)</label>
              <input
                data-testid="prazo-input-obs"
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                placeholder="Ex: reagendado com o cliente"
                className={inputClass}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              data-testid="prazo-remove-btn"
              onClick={removerPrazo}
              disabled={savingPrazo}
              className="bg-white border border-[#E4E4E7] hover:border-[#F43F5E] hover:text-[#F43F5E] text-[#52525B] px-3 py-2 text-sm font-medium rounded-md flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash2 size={13} />
              Remover
            </button>
            <button
              data-testid="prazo-save-btn"
              onClick={salvarPrazo}
              disabled={savingPrazo}
              className="bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-2 text-sm font-semibold rounded-md disabled:opacity-60"
            >
              {savingPrazo ? "Salvando…" : "Salvar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Adicionar etapa manual */}
      <Dialog open={eventoOpen} onOpenChange={setEventoOpen}>
        <DialogContent data-testid="evento-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Adicionar etapa manual</DialogTitle>
            <DialogDescription>
              Lança um status manualmente na linha do tempo da NF {nota.numero_nota}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Categoria</label>
                <select
                  data-testid="evento-input-categoria"
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  className={inputClass}
                >
                  {CATEGORIAS.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Código (opcional)</label>
                <input
                  data-testid="evento-input-codigo"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  placeholder="Ex: 01"
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Data e hora</label>
              <input
                data-testid="evento-input-data"
                type="datetime-local"
                value={dataEvento}
                onChange={(e) => setDataEvento(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Descrição</label>
              <input
                data-testid="evento-input-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Ex: Entrega confirmada por comprovante recebido"
                className={inputClass}
              />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="evento-save-btn"
              onClick={salvarEvento}
              disabled={savingEvento}
              className="bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-2 text-sm font-semibold rounded-md disabled:opacity-60"
            >
              {savingEvento ? "Adicionando…" : "Adicionar etapa"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotaActions;
